import { useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { SiteReview, SiteStats } from '../../api/queries/site';
import type { Hall } from '../../api/types';
import { Reveal } from '../../components/Reveal';
import { Stars } from '../../components/Stars';
import { img, imgSet, type PhotoKey } from '../../content/imagery';
import { ARTICLES, type Article } from '../../content/inspiration';
import { OCCASIONS, fitsOccasion, suits } from '../../content/occasions';
import { useT, type Key } from '../../i18n';
import { useFavorites } from '../../lib/favorites';
import { extraAmenities } from '../../lib/facilities';
import { dayFmt, moneyShort } from '../../lib/format';
import { useMaster } from '../../lib/master';
import { startingPrice } from '../../lib/price';
import { cityGroups, prefersReducedMotion, useCountUp, useScrollFrame } from './data';
import { ArrowLink, Head, SecLabel, Split } from './ui';
import { isNewListing } from '../../lib/listing';
import { placeName } from '../../lib/places';

const iv = (i: number) => ({ '--i': i }) as CSSProperties;
const placeOf = (h: Hall) => [h.locality, h.city].filter(Boolean).join(', ') || h.location;

/* ------------------------------ 01 / promise ------------------------------ */

const TRUST: { icon: string; title: Key; body: Key }[] = [
  { icon: 'ri-shield-check-line', title: 'hmTrust1Title', body: 'hmTrust1Body' },
  { icon: 'ri-calendar-check-line', title: 'hmTrust2Title', body: 'hmTrust2Body' },
  { icon: 'ri-layout-column-line', title: 'hmTrust3Title', body: 'hmTrust3Body' },
  { icon: 'ri-wallet-3-line', title: 'hmTrust4Title', body: 'hmTrust4Body' },
];

export function Why({ n }: { n: number }) {
  const { t } = useT();
  return (
    <section className="why" id="why" aria-labelledby="why-title">
      <SecLabel n={n} label={t('hmWhyLabel')} note={t('hmWhyNote')} />
      <div className="wrap why-grid">
        <Reveal>
          <Split text={t('hmTrustTitle')} id="why-title" />
        </Reveal>
        <Reveal as="ul" bare className="why-list stagger">
          {TRUST.map((item, i) => (
            <li key={item.title} style={iv(i)}>
              <span className="why-ico" aria-hidden="true">
                <i className={`${item.icon} i`} />
              </span>
              <h3>{t(item.title)}</h3>
              <p>{t(item.body)}</p>
            </li>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------- featured venue index -------------------------- */

function useFacilities() {
  const { t } = useT();
  const { label } = useMaster();
  return (h: Hall) =>
    [
      h.has_ac && t('cardFullAc'),
      h.has_parking && t('cardParking'),
      h.has_lift && t('hallLift'),
      h.rooms > 0 && t('vcRooms', { count: h.rooms }),
      ...extraAmenities(h).slice(0, 2).map((a) => label('amenity', a)),
    ].filter(Boolean) as string[];
}

/**
 * "Venues worth a closer look": a big photo stage with a glass card on the
 * left, a list of names on the right. Hovering (or focusing) a row brings
 * that venue onto the stage; on touch screens the first tap selects, the
 * second opens the venue.
 */
export function FeaturedIndex({ n, halls }: { n: number; halls: Hall[] }) {
  const { t, locale } = useT();
  const { label } = useMaster();
  const fav = useFavorites();
  const facilities = useFacilities();
  const [cur, setCur] = useState(0);
  const hoverTimer = useRef<number>();
  const byPointer = useRef(false);
  const [canHover] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches);
  const [warm, setWarm] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  useEffect(() => {
    if (cur >= halls.length) setCur(0);
  }, [halls.length, cur]);

  // Warm up the other photos once the section is near.
  useEffect(() => {
    const el = root.current;
    if (!el || typeof IntersectionObserver === 'undefined') return setWarm(true);
    const io = new IntersectionObserver(
      ([en]) => {
        if (en.isIntersecting) {
          setWarm(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  if (!halls.length) return null;
  const active = halls[Math.min(cur, halls.length - 1)];
  const saved = fav.has(active.id);
  const total = String(halls.length).padStart(2, '0');

  return (
    <section className="section on-night fi-section" aria-labelledby="featured-title">
      <SecLabel n={n} label={t('hmFeatEyebrow')} note={t('hmFeatNote')} />
      <div className="wrap">
        <Head id="featured-title" title={t('hmFeatTitle')} sub={t('hmFeatSub')} action={{ to: '/venues', label: t('hmFeatAll') }} />
        <div className="fi is-smooth" ref={root}>
          <Reveal bare className="fi-stage reveal-img">
            <div className="fi-media">
              {halls.map((h, k) =>
                h.photos[0] ? (
                  <img
                    key={h.id}
                    className={k === cur ? 'is-active' : undefined}
                    src={h.photos[0]}
                    alt={h.name}
                    loading={k === 0 || k === cur || warm ? 'eager' : 'lazy'}
                    decoding="async"
                  />
                ) : (
                  <div key={h.id} className={`hm-noimg${k === cur ? ' is-active' : ''}`} aria-hidden="true">
                    <i className="ri-building-2-line" />
                  </div>
                ),
              )}
            </div>
            <button
              type="button"
              className="fav fi-fav"
              aria-pressed={saved}
              aria-label={saved ? t('vcUnsave', { name: active.name }) : t('vcSave', { name: active.name })}
              onClick={() => fav.toggle(active.id)}
            >
              <i className={`${saved ? 'ri-heart-fill' : 'ri-heart-line'} i`} aria-hidden="true" />
            </button>
            <span className="fi-count" aria-hidden="true">
              <b>{String(cur + 1).padStart(2, '0')}</b> / {total}
            </span>
            <div className="fi-card" aria-live="polite">
              {halls.map((h, k) => {
                const on = k === cur;
                const from = startingPrice(h);
                const amen = facilities(h);
                return (
                  <div key={h.id} className={`fi-panel${on ? ' is-on' : ''}`} aria-hidden={!on}>
                    <div className="fi-top">
                      <span className="kicker">{label('venue_type', h.category)}</span>
                      {h.review_count > 0 ? (
                        <span className="fi-rating">
                          <i className="ri-star-fill i" aria-hidden="true" />
                          {h.rating.toFixed(1)} <small>({t('hmSpotReviews', { count: h.review_count })})</small>
                        </span>
                      ) : (
                        isNewListing(h) && <span className="badge badge-new">{t('hmSpotNew')}</span>
                      )}
                    </div>
                    <h3>{h.name}</h3>
                    {placeOf(h) && (
                      <div className="fi-loc">
                        <i className="ri-map-pin-2-line i" aria-hidden="true" />
                        {placeOf(h)}
                      </div>
                    )}
                    {amen.length > 0 && (
                      <ul className="amen">
                        {amen.slice(0, 4).map((a) => (
                          <li key={a}>{a}</li>
                        ))}
                      </ul>
                    )}
                    <div className="fi-foot">
                      <dl className="fi-stats">
                        {h.capacity > 0 && (
                          <div>
                            <dt>{t('hmFeatCapacity')}</dt>
                            <dd>{t('hmGuests', { count: h.capacity.toLocaleString(locale) })}</dd>
                          </div>
                        )}
                        <div>
                          <dt>{t('hmFeatFrom')}</dt>
                          <dd>
                            {from ? (
                              <>
                                {moneyShort(from.amount)} <small>{t(from.unit)}</small>
                              </>
                            ) : (
                              t('hmSpotOnRequest')
                            )}
                          </dd>
                        </div>
                      </dl>
                      <Link className="btn btn-gold btn-sm" to={`/hall/${h.id}`} tabIndex={on ? undefined : -1}>
                        {t('hmFeatView')}
                        <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
          <Reveal as="ul" bare className="fi-list stagger" aria-label={t('hmFeatList')}>
            {halls.map((h, k) => {
              const from = startingPrice(h);
              const select = () => setCur(k);
              return (
                <li key={h.id} style={iv(k)}>
                  <Link
                    className="fi-row"
                    to={`/hall/${h.id}`}
                    aria-current={k === cur ? 'true' : 'false'}
                    onMouseEnter={
                      canHover
                        ? () => {
                            clearTimeout(hoverTimer.current);
                            hoverTimer.current = window.setTimeout(select, 90);
                          }
                        : undefined
                    }
                    onMouseLeave={canHover ? () => clearTimeout(hoverTimer.current) : undefined}
                    onPointerDown={() => (byPointer.current = true)}
                    onFocus={() => {
                      if (!byPointer.current) select();
                    }}
                    onClick={(e: MouseEvent) => {
                      byPointer.current = false;
                      if (!canHover && k !== cur) {
                        e.preventDefault();
                        select();
                      }
                    }}
                  >
                    <span className="fi-thumb">
                      {h.photos[0] ? <img src={h.photos[0]} alt="" loading="lazy" decoding="async" /> : <span className="hm-noimg" />}
                    </span>
                    <span className="fi-name">
                      <strong>{h.name}</strong>
                      <span>{[label('venue_type', h.category), placeOf(h)].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="fi-meta">
                      {h.capacity > 0 && <span>{t('hmGuests', { count: h.capacity.toLocaleString(locale) })}</span>}
                      {from && (
                        <span>
                          {moneyShort(from.amount)} {t(from.unit)}
                        </span>
                      )}
                    </span>
                    <span className="fi-go" aria-hidden="true">
                      <i className="ri-arrow-right-line i" />
                    </span>
                  </Link>
                </li>
              );
            })}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------- occasions: horizontal gallery -------------------- */

/**
 * The occasion cards in a sideways row. This used to pin the section and turn
 * the page's vertical scroll into sideways motion, so reaching it held the
 * visitor until every card had passed. It is now a plain scrolling row: the
 * page keeps scrolling down, and the arrows (or a swipe / trackpad) move along.
 */
export function OccasionsScroll({ n, halls }: { n: number; halls: Hall[] }) {
  const { t } = useT();
  const view = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ start: true, end: false });

  const sync = () => {
    const v = view.current;
    if (!v) return;
    const max = v.scrollWidth - v.clientWidth;
    const p = max > 0 ? v.scrollLeft / max : 0;
    bar.current?.style.setProperty('--hp', p.toFixed(3));
    setEdge({ start: v.scrollLeft <= 4, end: v.scrollLeft >= max - 4 });
  };
  useEffect(() => {
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, []);

  const step = (dir: 1 | -1) => {
    const v = view.current;
    if (!v) return;
    const card = v.querySelector<HTMLElement>('.hs-card');
    v.scrollBy({ left: dir * ((card?.offsetWidth ?? 400) + 20), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  };

  return (
    <section className="hs" aria-labelledby="occ-title">
      <div className="hs-sticky">
        <SecLabel n={n} label={t('hmOccEyebrow')} note={t('hmOccNote')} />
        <div className="wrap">
          <Head id="occ-title" title={t('hmOccTitle')} sub={t('hmOccSub')} action={{ to: '/occasions', label: t('hmOccAll') }} />
        </div>
        <div className="hs-viewport" ref={view} onScroll={sync}>
          <div className="hs-track">
            {OCCASIONS.map((o) => {
              const count = halls.filter((h) => fitsOccasion(h, o)).length;
              return (
                <Link className="hs-card" to={`/occasions/${o.slug}`} key={o.slug}>
                  <img
                    src={img(o.photo, 1100)}
                    srcSet={imgSet(o.photo, [600, 1100])}
                    sizes="(max-width: 720px) 82vw, 560px"
                    alt=""
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="hs-label">
                    {t('hmOccLabel')}
                    {count > 0 && ` · ${t('hmVenueCount', { count })}`}
                  </span>
                  <h3>{t(o.title)}</h3>
                  <p>{t(o.body)}</p>
                  <span className="hs-go" aria-hidden="true">
                    <i className="ri-arrow-right-up-line i" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
        <div className="wrap">
          <div className="hs-foot">
            <div className="hs-progress" ref={bar} aria-hidden="true">
              <span />
            </div>
            <div className="carousel-btns">
              <button type="button" className="icon-btn" onClick={() => step(-1)} disabled={edge.start} aria-label={t('previous')}>
                <i className="ri-arrow-left-line" aria-hidden="true" />
              </button>
              <button type="button" className="icon-btn" onClick={() => step(1)} disabled={edge.end} aria-label={t('next')}>
                <i className="ri-arrow-right-line" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- statement with floating photo collage -------------------- */

const FLOATERS: { key: PhotoKey; x: string; y: string; w: string; s: number }[] = [
  { key: 'ballroomGrand', x: '6%', y: '10%', w: '14vw', s: 0.25 },
  { key: 'toast', x: '76%', y: '6%', w: '11vw', s: 0.45 },
  { key: 'weddingBouquet', x: '1%', y: '50%', w: '12vw', s: 0.6 },
  { key: 'concertStage', x: '84%', y: '46%', w: '12vw', s: 0.3 },
  { key: 'receptionTable', x: '18%', y: '70%', w: '10vw', s: 0.5 },
  { key: 'weddingConfetti', x: '66%', y: '68%', w: '10vw', s: 0.35 },
];

export function Statement() {
  const { t } = useT();
  const box = useRef<HTMLDivElement>(null);

  useScrollFrame(() => {
    const el = box.current;
    if (!el || prefersReducedMotion()) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--fy', `${((r.top + r.height / 2 - window.innerHeight / 2) * -0.6).toFixed(1)}px`);
  }, []);

  return (
    <Reveal as="section" bare className="statement" aria-labelledby="band-title">
      <svg className="orbit" viewBox="0 0 800 800" aria-hidden="true">
        <g className="orbit-globe" fill="none" stroke="currentColor" strokeWidth="1.2">
          <circle cx="400" cy="400" r="210" />
          <ellipse cx="400" cy="400" rx="150" ry="210" />
          <ellipse cx="400" cy="400" rx="80" ry="210" />
          <ellipse cx="400" cy="400" rx="210" ry="70" />
          <ellipse cx="400" cy="400" rx="210" ry="140" />
          <line x1="190" y1="400" x2="610" y2="400" />
          <line x1="400" y1="190" x2="400" y2="610" />
        </g>
        <ellipse
          className="orbit-ring"
          cx="400"
          cy="400"
          rx="380"
          ry="96"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          transform="rotate(-18 400 400)"
        />
      </svg>
      <div className="floaters" aria-hidden="true" ref={box}>
        {FLOATERS.map((f, i) => (
          <span key={f.key} className="floater" style={{ '--x': f.x, '--y': f.y, '--w': f.w, '--s': f.s, '--i': i } as CSSProperties}>
            <img src={img(f.key, 400, { h: 500 })} alt="" loading="lazy" decoding="async" />
          </span>
        ))}
      </div>
      <div className="wrap statement-inner">
        <Reveal as="span" className="kicker">
          {t('hmPromoEyebrow')}
        </Reveal>
        <Split text={t('hmPromoTitle')} id="band-title" />
        <Reveal className="statement-copy">
          <p>{t('hmPromoBody')}</p>
        </Reveal>
        <Reveal>
          <Link className="btn btn-primary" to="/venues">
            {t('hmPromoCta')}
            <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </Reveal>
  );
}

/* ------------------------------ how it works ------------------------------- */

const STEPS: { title: Key; body: Key }[] = [
  { title: 'hmStep1Title', body: 'hmStep1Body' },
  { title: 'hmStep2Title', body: 'hmStep2Body' },
  { title: 'hmStep3Title', body: 'hmStep3Body' },
  { title: 'hmStep4Title', body: 'hmStep4Body' },
];

export function HowItWorks({ n }: { n: number }) {
  const { t } = useT();
  return (
    <section className="section" id="how" tabIndex={-1} aria-labelledby="how-title">
      <SecLabel n={n} label={t('hmHowEyebrow')} note={t('hmHowNote')} />
      <div className="wrap rules">
        <Reveal className="rules-aside">
          <Split text={t('hmHowTitle')} id="how-title" />
          <p>{t('hmHowSub')}</p>
          <ArrowLink to="/about#how" label={t('hmHowGuide')} />
        </Reveal>
        <Reveal as="ol" bare className="rows stagger">
          {STEPS.map((s, i) => (
            <li key={s.title} style={iv(i)}>
              <span className="row-num" aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              <div>
                <h3>{t(s.title)}</h3>
                <p>{t(s.body)}</p>
              </div>
              <span className="row-k">{t('hmStepN', { n: i + 1 })}</span>
            </li>
          ))}
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------- spotlight with tilt --------------------------- */

const CATERING_KEY: Record<Hall['catering'], Key | null> = {
  none: null,
  inhouse: 'cateringInhouse',
  outside: 'cateringOutside',
  both: 'cateringBoth',
};

export function Spotlight({ n, hall }: { n: number; hall: Hall | null }) {
  const { t, locale } = useT();
  const { label } = useMaster();
  const [active, setActive] = useState(0);
  const [nonce, setNonce] = useState(0);
  const frame = useRef<HTMLDivElement>(null);
  const media = useRef<HTMLDivElement>(null);
  const photos = useMemo(() => (hall?.photos ?? []).slice(0, 4), [hall]);

  useEffect(() => setActive(0), [hall?.id]);
  useEffect(() => {
    if (photos.length < 2 || prefersReducedMotion()) return;
    const id = window.setInterval(() => setActive((i) => (i + 1) % photos.length), 4200);
    return () => clearInterval(id);
  }, [photos.length, nonce]);

  // The photo leans back and straightens as it scrolls up into view.
  useScrollFrame(() => {
    const el = frame.current;
    const m = media.current;
    if (!el || !m || prefersReducedMotion()) return;
    const vh = window.innerHeight;
    const r = el.getBoundingClientRect();
    const p = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.75)));
    m.style.setProperty('--tilt', `${(34 * (1 - p)).toFixed(2)}deg`);
    m.style.setProperty('--tscale', (0.86 + 0.14 * p).toFixed(3));
  }, [hall?.id]);

  if (!hall || photos.length === 0) return null;

  const from = startingPrice(hall);
  const place = placeOf(hall);
  const catering = CATERING_KEY[hall.catering];
  const facilities = [
    hall.has_ac && t('hmSpotAc'),
    hall.has_parking && (hall.car_parking > 0 ? t('hmSpotCarParking', { count: hall.car_parking }) : t('hmSpotParking')),
    hall.has_lift && t('hmSpotLift'),
    hall.rooms > 0 && t('vcRooms', { count: hall.rooms }),
    catering && t('hmSpotCatering', { value: t(catering) }),
    ...extraAmenities(hall),
  ]
    .filter(Boolean)
    .slice(0, 8) as string[];

  return (
    <section className="section case-sec spot-sec" aria-labelledby="spot-title">
      <SecLabel n={n} label={t('hmSpotEyebrow')} note={[hall.name, hall.city].filter(Boolean).join(', ')} />
      <div className="wrap">
        <Head id="spot-head" title={t('hmSpotTitle')} action={{ to: `/hall/${hall.id}`, label: t('hmSpotCta') }} />
        <div className="case-frame" ref={frame}>
          <div className="case-media" ref={media}>
            {photos.map((p, i) => (
              <img
                key={p + i}
                className={i === active ? 'is-active' : undefined}
                src={p}
                alt={`${hall.name} (${i + 1}/${photos.length})`}
                loading="lazy"
                decoding="async"
              />
            ))}
            {photos.length > 1 && (
              <div className="spot-dots">
                {photos.map((p, i) => (
                  <button
                    type="button"
                    key={p + i}
                    aria-label={t('hmSpotPhoto', { n: i + 1, name: hall.name })}
                    aria-current={i === active ? 'true' : undefined}
                    onClick={() => {
                      setActive(i);
                      setNonce((x) => x + 1);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
          {hall.category && (
            <span className="case-tag">
              <i />
              {label('venue_type', hall.category)}
            </span>
          )}
        </div>
        <div className="case-body">
          <Reveal>
            <h3 id="spot-title">{hall.name}</h3>
            <div className="spot-loc">
              {place && (
                <span>
                  <i className="ri-map-pin-2-line i" aria-hidden="true" />
                  {place}
                </span>
              )}
              {hall.review_count > 0 ? (
                <span>
                  <span role="img" aria-label={t('hmRevRated', { rating: hall.rating.toFixed(1) })}>
                    <Stars rating={hall.rating} />
                  </span>
                  &nbsp;{hall.rating.toFixed(1)} ({t('hmSpotReviews', { count: hall.review_count })})
                </span>
              ) : (
                <span>
                  <i className="ri-sparkling-line i" aria-hidden="true" />
                  {t('hmSpotNew')}
                </span>
              )}
            </div>
          </Reveal>
          <Reveal style={{ '--d': '.1s' } as CSSProperties}>
            <dl className="case-facts">
              {hall.capacity > 0 && (
                <div>
                  <dt>{t('hmSpotCapacity')}</dt>
                  <dd>{t('hmSpotGuests', { count: hall.capacity.toLocaleString(locale) })}</dd>
                </div>
              )}
              <div>
                <dt>{t('hmSpotFrom')}</dt>
                <dd>
                  {from ? (
                    <>
                      {moneyShort(from.amount)} <small>{t(from.unit)}</small>
                    </>
                  ) : (
                    t('hmSpotOnRequest')
                  )}
                </dd>
              </div>
            </dl>
            {facilities.length > 0 && (
              <>
                <div className="offers-title">
                  <h4>{t('hmSpotFacilities')}</h4>
                </div>
                <ul className="amen case-amen">
                  {facilities.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </>
            )}
            <Link className="btn btn-primary" to={`/hall/${hall.id}`}>
              {t('hmSpotCta')}
              <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------- locations -------------------------------- */

const CITY_FALLBACK: PhotoKey[] = ['conventionHall', 'auditoriumRed', 'banquetRounds', 'theatreTiers'];

export function Locations({ n, halls }: { n: number; halls: Hall[] }) {
  const { t, lang } = useT();
  const { label } = useMaster();
  const groups = cityGroups(halls).slice(0, 4);
  if (!groups.length) return null;
  // Cards sit in pairs; an odd one out is joined by the "your city next?" invite.
  const invite = groups.length % 2 === 1;
  return (
    <section className="section loc-sec" aria-labelledby="loc-title">
      <SecLabel n={n} label={t('hmLocEyebrow')} note={t('hmLocNote')} />
      <div className="wrap">
        <Head id="loc-title" title={t('hmLocTitle')} sub={t('hmLocSub')} action={{ to: '/locations', label: t('hmLocAll') }} />
        <div className="loc-grid">
          {groups.map((g, i) => {
            const fb = CITY_FALLBACK[i % CITY_FALLBACK.length];
            return (
              <Reveal key={g.city} style={{ '--d': `${(i % 2) * 0.1}s` } as CSSProperties} className="hm-loc-cell">
                <Link className="loc-card" to={`/venues?location=${encodeURIComponent(g.city)}`}>
                  {g.photo ? (
                    <img src={g.photo} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <img src={img(fb, 1200)} srcSet={imgSet(fb, [700, 1200])} sizes="(max-width: 1080px) 92vw, 760px" alt="" loading="lazy" decoding="async" />
                  )}
                  <h3>{placeName(g.city, lang)}</h3>
                  <div className="loc-row">
                    {g.categories.length > 0 && <p>{g.categories.map((c) => label('venue_type', c)).join(', ')}</p>}
                    <span className="btn btn-dot btn-sm">
                      {t('hmVenueCount', { count: g.count })}
                      <span className="dot" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
          {invite && (
            <Reveal style={{ '--d': '.1s' } as CSSProperties} className="hm-loc-cell">
              {/* This section is for guests, so the spare cell sends them on to
                  every venue; inviting owners lives in the footer and the CTA. */}
              <Link className="next-city" to="/venues">
                <span className="pin-pulse" aria-hidden="true">
                  <i className="ri-search-line i" />
                </span>
                <div>
                  <h3>{t('hmLocBrowseTitle')}</h3>
                  <p>{t('hmLocBrowseBody')}</p>
                </div>
                <ArrowLink as="span" to="" label={t('hmLocBrowseCta')} />
              </Link>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------- event types ------------------------------- */

export function EventTypes({ n, halls }: { n: number; halls: Hall[] }) {
  const { t } = useT();
  const master = useMaster();
  // The admin's active event types, so one added there is listed here too.
  const types = master.options('event_type').map((e) => e.value).filter((e) => e !== 'other');
  return (
    <section className="section on-night etypes-sec" aria-labelledby="et-title">
      <SecLabel n={n} label={t('hmEvEyebrow')} note={t('hmEvNote', { count: types.length })} />
      <div className="wrap">
        <Head id="et-title" title={t('hmEvTitle')} sub={t('hmEvSub')} action={{ to: '/occasions', label: t('hmOccAll') }} />
        <Reveal bare className="et-grid stagger">
          {types.map((ev, i) => {
            const count = halls.filter((h) => suits(h, ev)).length;
            return (
              <Link className="et-link" to={`/venues?event=${encodeURIComponent(ev)}`} key={ev} style={iv(i)}>
                <span className="et-name">{master.label('event_type', ev)}</span>
                <span className="et-count">{count > 0 ? t('hmVenueCount', { count }) : ''}</span>
                <span className="et-go" aria-hidden="true">
                  <i className="ri-arrow-right-up-line i" />
                </span>
              </Link>
            );
          })}
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------------- guides ---------------------------------- */

const ARTICLE_CAT: Record<Article['category'], Key> = {
  planning: 'hmCatPlanning',
  weddings: 'hmCatWeddings',
  corporate: 'hmCatCorporate',
  venues: 'hmCatVenues',
};

export function Guides({ n }: { n: number }) {
  const { t, lang } = useT();
  const items = ARTICLES.slice(0, 4);
  if (!items.length) return null;
  return (
    <section className="section guides-sec" aria-labelledby="guides-title">
      <SecLabel n={n} label={t('hmInspEyebrow')} note={t('hmInspNote')} />
      <div className="wrap">
        <Head id="guides-title" title={t('hmInspTitle')} sub={t('hmInspSub')} action={{ to: '/inspiration', label: t('hmInspAll') }} />
      </div>
      <div className="col-strip">
        {items.map((a, i) => (
          <Link className="col-card" to={`/inspiration/${a.slug}`} key={a.slug} style={iv(i)}>
            <span className="col-head">
              <span>{t(ARTICLE_CAT[a.category])}</span>
              <span>{t('hmInspMins', { count: a.readMins })}</span>
            </span>
            <span className="col-img">
              <img src={img(a.photo, 640)} srcSet={imgSet(a.photo, [400, 640])} sizes="(max-width: 720px) 76vw, 25vw" alt="" loading="lazy" decoding="async" />
            </span>
            <h3>{a.title[lang]}</h3>
            <p>{a.excerpt[lang]}</p>
            <ArrowLink as="span" to="" label={t('hmInspRead')} />
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------ reviews (big quote) ------------------------------ */

export function Reviews({ n, reviews }: { n: number; reviews: SiteReview[] | undefined }) {
  const { t } = useT();
  const [i, setI] = useState(0);
  const list = reviews ?? [];
  if (!list.length) return null;
  const r = list[Math.min(i, list.length - 1)];
  const long = r.comment.length > 110;
  return (
    <section className="section reviews-sec" aria-labelledby="rev-title">
      <SecLabel n={n} label={t('hmRevEyebrow')} note={t('hmRevNote')} />
      <div className="wrap big-quote">
        <h2 id="rev-title">{t('hmRevTitle')}</h2>
        <Reveal as="figure" key={r.id}>
          <span role="img" aria-label={t('hmRevRated', { rating: r.rating })}>
            <Stars rating={r.rating} />
          </span>
          <blockquote className={long ? 'is-long' : undefined}>“{r.comment}”</blockquote>
          <figcaption>
            <span className="q-avatar" aria-hidden="true">
              {(r.name || '?').charAt(0).toUpperCase()}
            </span>
            <span>
              <strong>{r.name}</strong> {t('hmRevBooked')}{' '}
              <Link to={`/hall/${r.hall_id}`}>
                {r.hall_name}
                {r.city ? `, ${r.city}` : ''}
              </Link>
              {Number.isFinite(r.created_at) && r.created_at > 0 && (
                <>
                  {' · '}
                  <time dateTime={new Date(r.created_at).toISOString()}>{dayFmt(r.created_at)}</time>
                </>
              )}
            </span>
          </figcaption>
        </Reveal>
        {list.length > 1 && (
          <div className="hm-quote-dots">
            {list.map((x, k) => (
              <button
                type="button"
                key={x.id}
                aria-label={t('hmRevShow', { n: k + 1, total: list.length })}
                aria-current={k === i ? 'true' : undefined}
                onClick={() => setI(k)}
              />
            ))}
          </div>
        )}
        <Reveal className="quote-note">
          <p>{t('hmRevSub')}</p>
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------------- numbers --------------------------------- */

function Num({ value, decimals = 0, label, note }: { value: number; decimals?: number; label: string; note?: string }) {
  const { locale } = useT();
  const { ref, value: shown } = useCountUp<HTMLDivElement>(value, decimals);
  const fmt = (x: number) => x.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return (
    <div>
      <div className="num" ref={ref}>
        <span aria-hidden="true">{fmt(shown)}</span>
        <span className="sr">{fmt(value)}</span>
      </div>
      <div className="num-k">{label}</div>
      {note && <div className="num-s">{note}</div>}
    </div>
  );
}

export function numberItems(stats: SiteStats | undefined, t: ReturnType<typeof useT>['t']) {
  const items: { key: string; value: number; decimals?: number; label: string; note?: string }[] = [];
  if (!stats) return items;
  if (stats.venues > 0) items.push({ key: 'v', value: stats.venues, label: t('hmFigVenues', { count: stats.venues }), note: t('hmFigVenuesNote') });
  if (stats.cities > 0) items.push({ key: 'c', value: stats.cities, label: t('hmFigCities', { count: stats.cities }), note: t('hmFigCitiesNote') });
  if (stats.events > 0) items.push({ key: 'e', value: stats.events, label: t('hmFigEvents', { count: stats.events }), note: t('hmFigEventsNote') });
  if (stats.reviews > 0 && stats.rating > 0)
    items.push({ key: 'r', value: stats.rating, decimals: 1, label: t('hmFigRating'), note: t('hmFigRatingNote', { count: stats.reviews }) });
  return items;
}

export function Numbers({ n, stats }: { n: number; stats: SiteStats | undefined }) {
  const { t } = useT();
  const items = numberItems(stats, t);
  if (!items.length) return null;
  return (
    <section className="section is-tight numbers-sec" aria-labelledby="num-title">
      <SecLabel n={n} label={t('hmFigEyebrow')} note={t('hmFigNote')} />
      <div className="wrap">
        <Head id="num-title" title={t('hmFigTitle')} />
        <Reveal className={`numbers is-stats hm-n${items.length}`}>
          {items.map(({ key, ...f }) => (
            <Num key={key} {...f} />
          ))}
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------------- ring CTA -------------------------------- */

export function RingCta() {
  const { t } = useT();
  const ring = useRef<SVGSVGElement>(null);

  useScrollFrame(() => {
    const el = ring.current;
    if (!el || prefersReducedMotion()) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--ring-r', `${((r.top - window.innerHeight) * 0.06).toFixed(2)}deg`);
  }, []);

  return (
    <section className="ring-cta" aria-labelledby="cta-title">
      <svg className="ring" viewBox="0 0 1000 1000" aria-hidden="true" ref={ring}>
        <defs>
          <path id="hm-ring-path" d="M500,500 m-410,0 a410,410 0 1,1 820,0 a410,410 0 1,1 -820,0" />
        </defs>
        <text xmlSpace="preserve">
          <textPath href="#hm-ring-path" textLength="2560" lengthAdjust="spacing">
            {t('hmRingText')}
          </textPath>
        </text>
      </svg>
      <div className="wrap ring-inner">
        <Reveal as="span" className="kicker">
          {t('hmFinalEyebrow')}
        </Reveal>
        <Split text={t('hmFinalTitle')} id="cta-title" />
        <Reveal>
          <p>{t('hmFinalBody')}</p>
        </Reveal>
        <Reveal className="hero-ctas">
          <Link className="btn btn-primary" to="/venues">
            {t('hmFinalExplore')}
            <i className="ri-arrow-right-line i i-move" aria-hidden="true" />
          </Link>
          <Link className="btn btn-dot" to="/login?signup=venue">
            {t('hmFinalList')}
            <span className="dot" aria-hidden="true" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
